import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Card, Table, Badge, Form, Button } from "react-bootstrap";
import { GRADIENTS } from "utils/theme";
import Useaxios from "utils/Useaxios";
import { API } from "utils/Apiroutes";
import usePagination from "hooks/usePagination";
import Pagination from "components/ui/Pagination";
import ActionButtons from "components/ui/ActionButtons";
import { showSuccessAlert, showErrorAlert, showDeleteConfirmation } from "utils/swalAlert";
import TableFilter from "components/ui/TableFilter";
import {
  getGlobalCurrency,
  convertPrice,
  setExchangeRate
} from "../utils/currency.js";

import CurrencyDropdown from "./ui/CurrencyDropdown.js";

function PendingTransactions() {
  const { fetchData } = Useaxios();
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(getGlobalCurrency());

  useEffect(() => {
  const fetchExchangeRate = async () => {
    try {
      const res = await fetchData({
        url: API.getExchangeRate,
        method: "GET",
        showMessage: false
      });

      if (res?.success && res?.data?.exchangeRate) {
        setExchangeRate(Number(res.data.exchangeRate));
      } else {
        setExchangeRate(1);
      }

    } catch (error) {
      setExchangeRate(1);
    }
  };

  fetchExchangeRate();
}, []);

useEffect(() => {
  const handleCurrencyChange = () => {
    setSelectedCurrency(getGlobalCurrency());
  };

  window.addEventListener("currencyChanged", handleCurrencyChange);

  return () => {
    window.removeEventListener("currencyChanged", handleCurrencyChange);
  };
}, []);

  const fetchPendingTransactions = useCallback(async (params) => {
    const res = await fetchData({
      url: API.getTransactions,
      method: 'GET',
      params: { ...params, status: 'PENDING' },
      showMessage: false
    });
    
    return res;
  }, []);

  const {
    data: transactions,
    loading,
    currentPage,
    totalPages,
    totalItems,
    pageSize,
    filters,
    goToPage,
    nextPage,
    prevPage,
    changePageSize,
    updateFilters,
    clearFilters
  } = usePagination(fetchPendingTransactions, { machineId: '' });

  const pendingStats = {
    total: transactions?.length || 0,
    totalAmount: (transactions && Array.isArray(transactions)) ? transactions.reduce((sum, t) => sum + t.totalAmount, 0) : 0
  };

  const filterFields = [
    {
      key: 'machineId',
      label: 'Machine ID',
      type: 'text',
      placeholder: 'Filter by Machine ID...'
    }
  ];


  const handleStatusUpdate = async (transaction, newStatus) => {
    showDeleteConfirmation({
      title: `Mark as ${newStatus}?`,
      text: `Are you sure you want to mark this transaction as ${newStatus.toLowerCase()}?`,
      itemName: `Transaction ${transaction._id.slice(-8)}`,
      onConfirm: async () => {
        try {
          const res = await fetchData({
            url: `${API.updateTransaction}/${transaction._id}`,
            method: 'PUT',
            data: { status: newStatus }
          });
          if (res.success) {
            showSuccessAlert(`Transaction marked as ${newStatus.toLowerCase()} successfully!`);
            window.location.reload();
          }
        } catch (error) {
          showErrorAlert('Failed to update transaction status');
        }
      }
    });
  };

  return (
    <Container fluid className="py-4">
      <Row>
        <Col md="12">
          <Card className="shadow border-0 rounded-3 overflow-hidden">
            <div style={{ background: GRADIENTS.primary }} className="text-white py-3 px-4">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="mb-0 fw-semibold" style={{ fontSize: 'clamp(1rem, 4vw, 1.5rem)' }}>
                    <i className="fas fa-clock me-2"></i>
                    <span className="d-none d-sm-inline">Pending Transactions</span>
                    <span className="d-inline d-sm-none">Pending</span>
                  </h4>
                  <p className="mb-0 opacity-75 mt-1 d-none d-md-block" style={{ fontSize: '0.9rem' }}>
                    Monitor all pending transactions requiring attention
                  </p>
                </div>
                
               <div className="d-flex gap-2 align-items-center">

    {/* 🔥 Currency Dropdown (Same as Product Page) */}
    <CurrencyDropdown />

    <Button
      variant="light"
      size="sm"
      onClick={() => setShowFilters(!showFilters)}
      className="fw-medium d-flex align-items-center"
    >
      <i className="fas fa-filter me-1"></i>
      <span className="d-none d-md-inline">Filters</span>
      {Object.values(filters).filter(v => v).length > 0 && (
        <Badge bg="danger" className="ms-2">
          {Object.values(filters).filter(v => v).length}
        </Badge>
      )}
    </Button>

  </div>
              </div>
            </div>

            {showFilters && (
              <div className="px-4 pt-3 border-bottom">
                <TableFilter
                  filterConfig={filterFields}
                  filters={filters}
                  onFilterChange={updateFilters}
                  onClearFilters={clearFilters}
                />
              </div>
            )}

            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-warning" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading pending transactions...</p>
                </div>
              ) : !transactions || transactions.length === 0 ? (
                <div className="text-center py-5">
                  <div className="display-1 text-muted mb-3 opacity-50">
                    <i className="fas fa-clock"></i>
                  </div>
                  <h4 className="text-muted mb-3">No Pending Transactions</h4>
                  <p className="text-muted mb-4">All transactions have been processed</p>
                </div>
              ) : (
                <>
                  {/* Statistics Cards */}
                  <div className="p-4 bg-light border-bottom">
                    <Row className="g-3">
                      <Col md={6}>
                        <div className="stat-card">
                          <div className="d-flex align-items-center">
                            <div className="stat-icon bg-warning bg-opacity-10 text-warning rounded-circle p-3 me-3">
                              <i className="fas fa-clock fa-lg"></i>
                            </div>
                            <div>
                              <h3 className="fw-bold mb-0">{pendingStats.total}</h3>
                              <small className="text-muted">Pending Transactions</small>
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="stat-card">
                          <div className="d-flex align-items-center">
                            <div className="stat-icon bg-info bg-opacity-10 text-info rounded-circle p-3 me-3">
                              <i className="fas fa-dollar-sign fa-lg"></i>
                            </div>
                            <div>
<h3 className="fw-bold mb-0">
  {selectedCurrency}{" "}
  {convertPrice(pendingStats.totalAmount, "LRD").toFixed(2)}
</h3>                              <small className="text-muted">Pending Amount</small>
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>

                  {/* Pending Transactions Table */}
                  <div className="table-responsive">
                    <Table hover className="mb-0 align-middle">
                      <thead className="bg-light">
                        <tr>
                          <th className="border-0 ps-4">Transaction ID</th>
                          <th className="border-0">Machine ID</th>
                          <th className="border-0">Product</th>
                          <th className="border-0 text-center">Qty</th>
                          <th className="border-0 text-end">Amount</th>
                          <th className="border-0 text-end">Tax</th>
                          <th className="border-0 text-end">Total</th>
                          <th className="border-0 text-center">Status</th>
                          <th className="border-0">Payment</th>
                          <th className="border-0">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions && transactions.map((transaction) => (
                          <tr key={transaction._id} className="align-middle">
                            <td className="ps-4">
                              <div className="d-flex align-items-center">
                                <div className="transaction-icon bg-warning bg-opacity-10 text-warning rounded-circle p-2 me-3">
                                  <i className="fas fa-clock"></i>
                                </div>
                                <div>
                                  <h6 className="mb-0 fw-semibold">{transaction.transactionId}</h6>
                                 
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <i className="fas fa-robot text-muted me-2"></i>
                                <span className="fw-semibold">{transaction.machineId}</span>
                              </div>
                            </td>
                            <td>
                              {transaction.items.map((item, index) => (
                                <div key={index} className="mb-1">
                                  <span className="fw-medium">{item.productId.name}</span>
                                  <small className="text-muted d-block">${selectedCurrency}{" "}
{convertPrice(
  item.finalPrice,
  item.currencyType || "LRD"
).toFixed(2)} each</small>
                                </div>
                              ))}
                            </td>
                            <td className="text-center">
                              {transaction.items.map((item, index) => (
                                <div key={index} className="mb-1">
                                  <Badge bg="secondary" className="rounded-pill">{item.qty}</Badge>
                                </div>
                              ))}
                            </td>
                            <td className="text-end">
                              <span className="fw-semibold">${selectedCurrency}{" "}
{convertPrice(
  transaction.amount,
  transaction.currencyType || "LRD"
).toFixed(2)}</span>
                            </td>
                            <td className="text-end">
                              <span className="text-muted">${selectedCurrency}{" "}
{convertPrice(
  transaction.taxAmount,
  transaction.currencyType || "LRD"
).toFixed(2)}</span>
                            </td>
                            <td className="text-end">
                              <span className="fw-bold text-warning">${selectedCurrency}{" "}
{convertPrice(
  transaction.totalAmount,
  transaction.currencyType || "LRD"
).toFixed(2)}</span>
                            </td>
                            <td className="text-center">
                              <Badge
                                bg="warning"
                                className="rounded-pill px-3 py-2"
                                style={{ fontSize: '0.85rem' }}
                              >
                                <i className="fas fa-circle me-1" style={{ fontSize: '0.5rem' }}></i>
                                Pending
                              </Badge>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <i className="fas fa-credit-card text-muted me-2"></i>
                                <span className="text-capitalize">{transaction.paymentMethod}</span>
                              </div>
                              {transaction.momoRef && (
                                <small className="text-muted d-block">Ref: {transaction.momoRef.slice(-8)}</small>
                              )}
                            </td>
                            <td>
                              <div>
                                <span className="fw-medium">{new Date(transaction.createdAt).toLocaleDateString()}</span>
                                <small className="text-muted d-block">{new Date(transaction.createdAt).toLocaleTimeString()}</small>
                              </div>
                            </td>
                            <td className="text-center pe-4">
                              {/* <ActionButtons
                                onEdit={() => handleStatusUpdate(transaction, 'SUCCESS')}
                                onDelete={() => handleStatusUpdate(transaction, 'FAILED')}
                                showView={false}
                                size="sm"
                                variant="outline"
                              /> */}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}
            </Card.Body>

            {transactions && transactions.length > 0 && (
              <Card.Footer className="bg-light border-0 py-3 px-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  pageSize={pageSize}
                  onPageChange={goToPage}
                  onNextPage={nextPage}
                  onPrevPage={prevPage}
                  onPageSizeChange={changePageSize}
                />
              </Card.Footer>
            )}
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default PendingTransactions;